import React, { useState } from 'react';
import { XIcon, UploadIcon, ImageIcon } from 'lucide-react';
interface OrgUnitFormProps {
  orgUnit?: any;
  onClose: () => void;
  onSave: (data: any) => void;
  locale: string;
}
export function OrgUnitForm({
  orgUnit,
  onClose,
  onSave,
  locale
}: OrgUnitFormProps) {
  const [formData, setFormData] = useState({
    code: orgUnit?.code || '',
    name: orgUnit?.name || '',
    description: orgUnit?.description || '',
    orgUnitTypeId: orgUnit?.orgUnitTypeId || '',
    parentOrgUnitId: orgUnit?.parentOrgUnitId || '',
    isActive: orgUnit?.isActive ?? true,
    isOrgRoot: orgUnit?.isOrgRoot ?? false,
    participatesInGLCoding: orgUnit?.participatesInGLCoding ?? false,
    glCodeSegment: orgUnit?.glCodeSegment || '',
    currencyId: orgUnit?.currencyId || '',
    logoFileId: orgUnit?.logoFileId || null
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(orgUnit?.logoUrl || null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      // In a real app, you would upload the file and get a fileId
      // For now, we'll simulate with a generated ID
      setFormData({
        ...formData,
        logoFileId: `file-${Date.now()}`
      });
    }
  };
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData({
      ...formData,
      logoFileId: null
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validation: IsOrgRoot = true requires ParentOrgUnitId = null
    if (formData.isOrgRoot && formData.parentOrgUnitId) {
      alert('Organisation Root units cannot have a parent. Please remove the parent or uncheck Organisation Root.');
      return;
    }
    // Include logo file in save data if present
    const saveData = {
      ...formData,
      logoFile: logoFile // In real app, this would be handled by file upload service
    };
    onSave(saveData);
  };
  const handleOrgRootChange = (checked: boolean) => {
    setFormData({
      ...formData,
      isOrgRoot: checked,
      // If setting as org root, clear parent
      parentOrgUnitId: checked ? '' : formData.parentOrgUnitId
    });
  };
  return <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" data-id="element-19864">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-19865">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-19866">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-19867">
            {orgUnit ? 'Edit Organisation Unit' : 'Add Organisation Unit'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-19868">
            <XIcon className="h-6 w-6" data-id="element-19869" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6" data-id="element-19870">
          {/* Logo Upload */}
          <div data-id="element-19871">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-19872">
              Organisation Logo
            </h4>
            <div className="flex items-start space-x-4" data-id="element-19873">
              {/* Logo Preview */}
              <div className="flex-shrink-0" data-id="element-19874">
                {logoPreview ? <div className="relative flex items-center justify-center min-w-[96px]" data-id="element-19875">
                    <img src={logoPreview} alt="Logo preview" className="max-h-24 max-w-48 object-contain rounded-lg border-2 border-gray-300 bg-white p-2" data-id="element-19876" />
                    <button type="button" onClick={handleRemoveLogo} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-sm" data-id="element-19877">
                      <XIcon className="h-4 w-4" data-id="element-19878" />
                    </button>
                  </div> : <div className="h-24 w-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50" data-id="element-19879">
                    <ImageIcon className="h-8 w-8 text-gray-400" data-id="element-19880" />
                  </div>}
              </div>

              {/* Upload Button */}
              <div className="flex-1" data-id="element-19881">
                <label className="block" data-id="element-19882">
                  <span className="sr-only" data-id="element-19883">Choose logo file</span>
                  <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors cursor-pointer" data-id="element-19884">
                    <UploadIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19885" />
                    <span className="text-sm text-gray-600" data-id="element-19886">
                      {logoPreview ? 'Change logo' : 'Upload logo'}
                    </span>
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" data-id="element-19887" />
                  </div>
                </label>
                <p className="mt-2 text-xs text-gray-500" data-id="element-19888">
                  PNG, JPG, GIF up to 5MB. Square or rectangular logos
                  supported.
                </p>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div data-id="element-19889">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-19890">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-19891">
              <div data-id="element-19892">
                <label className="block text-sm font-medium text-gray-700" data-id="element-19893">
                  Code *
                </label>
                <input type="text" required value={formData.code} onChange={e => setFormData({
                ...formData,
                code: e.target.value
              })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="e.g., NY-HQ" data-id="element-19894" />
              </div>

              <div data-id="element-19895">
                <label className="block text-sm font-medium text-gray-700" data-id="element-19896">
                  Name *
                </label>
                <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="e.g., New York Headquarters" data-id="element-19897" />
              </div>

              <div className="sm:col-span-2" data-id="element-19898">
                <label className="block text-sm font-medium text-gray-700" data-id="element-19899">
                  Description
                </label>
                <textarea value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} rows={3} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Optional description" data-id="element-19900" />
              </div>

              <div data-id="element-19901">
                <label className="block text-sm font-medium text-gray-700" data-id="element-19902">
                  OrgUnit Type *
                </label>
                <select required value={formData.orgUnitTypeId} onChange={e => setFormData({
                ...formData,
                orgUnitTypeId: e.target.value
              })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-19903">
                  <option value="" data-id="element-19904">Select type...</option>
                  <option value="type-001" data-id="element-19905">Legal Entity</option>
                  <option value="type-002" data-id="element-19906">Physical Site</option>
                  <option value="type-003" data-id="element-19907">Department</option>
                  <option value="type-004" data-id="element-19908">Project</option>
                </select>
              </div>

              <div data-id="element-19909">
                <label className="block text-sm font-medium text-gray-700" data-id="element-19910">
                  Parent OrgUnit
                </label>
                <select value={formData.parentOrgUnitId} onChange={e => setFormData({
                ...formData,
                parentOrgUnitId: e.target.value
              })} disabled={formData.isOrgRoot} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed" data-id="element-19911">
                  <option value="" data-id="element-19912">None (Root level)</option>
                  <option value="org-001" data-id="element-19913">Global Technologies Inc.</option>
                  <option value="org-002" data-id="element-19914">New York Headquarters</option>
                  <option value="org-003" data-id="element-19915">San Francisco Office</option>
                </select>
                {formData.isOrgRoot && <p className="mt-1 text-xs text-gray-500" data-id="element-19916">
                    Organisation Root units cannot have a parent
                  </p>}
              </div>
            </div>
          </div>

          {/* GL Coding */}
          <div data-id="element-19917">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-19918">
              GL Coding (Optional)
            </h4>
            <div className="space-y-4" data-id="element-19919">
              <div className="flex items-center" data-id="element-19920">
                <input type="checkbox" checked={formData.participatesInGLCoding} onChange={e => setFormData({
                ...formData,
                participatesInGLCoding: e.target.checked
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-19921" />
                <label className="ml-2 block text-sm text-gray-900" data-id="element-19922">
                  Participates in GL Coding
                </label>
              </div>

              {formData.participatesInGLCoding && <div data-id="element-19923">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-19924">
                    GL Code Segment
                  </label>
                  <input type="text" value={formData.glCodeSegment} onChange={e => setFormData({
                ...formData,
                glCodeSegment: e.target.value
              })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="e.g., 1100" data-id="element-19925" />
                </div>}
            </div>
          </div>

          {/* Settings */}
          <div data-id="element-19926">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-19927">Settings</h4>
            <div className="space-y-4" data-id="element-19928">
              <div className="flex items-center" data-id="element-19929">
                <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({
                ...formData,
                isActive: e.target.checked
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-19930" />
                <label className="ml-2 block text-sm text-gray-900" data-id="element-19931">
                  Active
                </label>
              </div>

              <div data-id="element-19932">
                <div className="flex items-center" data-id="element-19933">
                  <input type="checkbox" checked={formData.isOrgRoot} onChange={e => handleOrgRootChange(e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-19934" />
                  <label className="ml-2 block text-sm text-gray-900" data-id="element-19935">
                    Organisation Root
                  </label>
                </div>
                <p className="mt-1 ml-6 text-xs text-gray-500" data-id="element-19936">
                  Root units cannot have a parent and must use an OrgUnit Type
                  that allows root level
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200" data-id="element-19937">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19938">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19939">
              {orgUnit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}